import { api, unwrap, type APIResponse } from "./client";

// Mirrors app/routers/campaign.py CampaignResponse.
export interface Campaign {
  campaign_id: number;
  name: string;
  status: string;
  service_flag: number;
  dial_method: number;
  channel: number;
  ring_time: number;
  try_allowed: number;
  callerid: string | null;
  callerid_name: string | null;
  // Caller-ID source fields — always present in API responses; optional here so
  // test fixtures and older callers don't need to specify them.
  callerid_flag?: number;
  callerid_mode?: string; // 'custom' | 'group' | 'anonymous'
  did_group_id?: number | null;
  callerid_random?: boolean;
  local_dial?: boolean;
  recording_file?: string | null;
  am_recording_file?: string | null;
  message_type?: number; // 0=voice broadcast, 1=press-1 IVR, 2=direct connect + VM drop
  type_touched?: boolean;
  transfer_extension_id?: number | null;
  connect_key?: string | null;
  optout_key?: string | null;
  am_action: string | null;
  contact_group_id: number | null;
  // Where press-1 connects the caller: 'pstn' (default — dial the extension's
  // outside forward number) or 'agents' (route to this campaign's agent
  // queue instead). Always present in API responses — absent campaign_detail
  // row reads back as 'pstn'.
  transfer_target?: "pstn" | "agents";
  // Carrier (LNP) filter fields — round-tripped from campaign_detail (see
  // _campaign_details_for), so an existing campaign's editor can pre-fill the
  // active filter instead of always opening to defaults. See CampaignInput.
  lnp_filter_mode?: string; // 'off' | 'exclude' | 'include'
  lnp_filter_carriers?: string; // comma-joined SPIDs
  lnp_require_mobile?: string; // '0' | '1'
  lnp_require_valid?: string; // '0' | '1'
  lnp_unknown_policy?: string;
  // Voicemail skip, round-tripped the same way so the editor pre-fills it.
  vm_skip_mode?: string; // 'off' | 'on'
  vm_skip_min_calls?: string; // answered calls of evidence required
  // Automatic dial pacing is adjusting this campaign's channel cap. Read-only
  // here; settings and history live under /api/pacing (see api/pacing.ts).
  pacing_enabled?: boolean;
}

export async function listCampaigns(status?: string): Promise<Campaign[]> {
  // Trailing slash required: the route is defined at "/" and FastAPI 307-redirects
  // "/campaigns" → "/campaigns/" (which can drop the auth header through proxies).
  const resp = await api.get<APIResponse<Campaign[]>>("/campaigns/", {
    params: status ? { campaign_status: status } : undefined,
  });
  return unwrap(resp.data);
}

// Mirrors app/routers/campaign.py CampaignCreate.
export interface CampaignInput {
  name: string;
  service_flag: number;
  dial_method: number;
  // Concurrency cap. Always sent on create; on update only when the operator
  // edited the field (see CampaignsPage.tsx) so a save cannot put a cap back
  // that automatic dial pacing has since moved.
  channel?: number;
  ring_time: number; // milliseconds
  /** Omitted when the operator did not touch the list this session.
   *  The update route re-feeds the spool whenever it receives this, and that
   *  feed de-duplicates PER CAMPAIGN — so sending it unchanged on a campaign
   *  that shares a list with another would re-queue the other's numbers and
   *  dial those people twice. */
  contact_group_id?: number | null;
  try_allowed: number;
  callerid: string | null;
  callerid_name: string | null;
  callerid_mode?: string; // 'custom' | 'group' | 'anonymous'
  did_group_id?: number | null;
  callerid_random?: boolean;
  local_dial?: boolean;
  recording_file?: string | null;
  am_recording_file?: string | null;
  message_type?: number; // 0=voice broadcast, 1=press-1 IVR, 2=direct connect + VM drop
  type_touched?: boolean;
  transfer_extension_id?: number | null;
  connect_key?: string | null;
  optout_key?: string | null;
  am_action: string | null;
  // Where press-1 connects the caller ('pstn' default | 'agents'). Only
  // include this key in a request when the operator actually touched the
  // control — an existing campaign's PUT /settings only writes fields it's
  // given, so omitting it leaves whatever routing is already set.
  transfer_target?: "pstn" | "agents";
  // Carrier (LNP) filter — the request body for create/update (see Campaign
  // above for the round-tripped read side).
  lnp_filter_mode?: string; // 'off' | 'exclude' | 'include'
  lnp_filter_carriers?: string; // comma-joined SPIDs
  lnp_require_mobile?: string; // '0' | '1'
  lnp_require_valid?: string; // '0' | '1'
  lnp_unknown_policy?: string;
  // Voicemail skip — numbers already known to answer with a machine every time.
  vm_skip_mode?: string; // 'off' | 'on'
  vm_skip_min_calls?: string; // answered calls of evidence required
}

export async function createCampaign(body: CampaignInput): Promise<number> {
  const resp = await api.post<APIResponse<{ campaign_id: number }>>("/campaigns/", body);
  return unwrap(resp.data).campaign_id;
}

// Partial update — PUT /{id}/settings accepts any subset.
export async function updateCampaign(id: number, body: Partial<CampaignInput>): Promise<void> {
  await api.put(`/campaigns/${id}/settings`, body);
}

export async function startCampaign(id: number): Promise<void> {
  await api.post(`/campaigns/${id}/start`);
}

/** Readable message for a failed start/stop.
 *
 * A start can be refused with a FastAPI `{detail: "..."}` (402 insufficient
 * credit, 409 wrong status) whose text is the whole point — without this the
 * page would show axios's opaque "Request failed with status code 402", which
 * is exactly the silent-failure that had an operator mash Start ~100 times on
 * an out-of-credit account (2026-08-20).
 */
export function campaignActionErrorMessage(err: unknown): string {
  const ax = err as {
    response?: { data?: { detail?: string; error?: string } };
    message?: string;
  };
  return (
    ax?.response?.data?.detail ||
    ax?.response?.data?.error ||
    ax?.message ||
    "Something went wrong. Please try again."
  );
}

export async function stopCampaign(id: number): Promise<void> {
  await api.post(`/campaigns/${id}/stop`);
}

export async function deleteCampaign(id: number): Promise<void> {
  await api.delete(`/campaigns/${id}`);
}

export interface DuplicateCampaignResult {
  campaign_id: number;
  source_campaign_id: number;
  name: string;
  status: string;
  contacts_queued: number;
}

export async function duplicateCampaign(
  id: number,
  name?: string,
): Promise<DuplicateCampaignResult> {
  const resp = await api.post<APIResponse<DuplicateCampaignResult>>(
    `/campaigns/${id}/duplicate`,
    name ? { name } : {},
  );
  return unwrap(resp.data);
}

export interface CampaignResetResult {
  campaign_id: number;
  campaign_name: string;
  status: string;
  reset: number;
  newly_added: number;
  dnc_suppressed: number;
  dnc_total: number;
  pending_total: number;
  total: number;
}

export async function resetCampaignList(id: number): Promise<CampaignResetResult> {
  const resp = await api.post<APIResponse<CampaignResetResult>>(`/campaigns/${id}/reset`);
  return unwrap(resp.data);
}

// Mirrors the raw dict from app/routers/report.py campaign_stats().
export interface CampaignStats {
  campaign_id: number;
  total_calls: number;
  total_cost: number;
  breakdown: {
    disposition: string | null;
    count: number;
    pct: number;
    total_billsec: number;
    total_cost: number;
  }[];
}

export async function getCampaignStats(campaignId: number): Promise<CampaignStats> {
  const resp = await api.get<CampaignStats>(`/reports/campaigns/${campaignId}/stats`);
  return resp.data;
}

// Mirrors app/routers/report.py campaign_dashboard().
export interface CampaignDashboard {
  campaign_id: number;
  window: string;
  kpis: {
    total_dialed: number;
    connected: number;
    connect_rate: number;
    transfers: number;
    transfer_rate: number;
    avg_duration_sec: number;
    active_channels: number;
    cps: number;
    total_cost: number;
    cost_per_call: number;
    cost_per_transfer: number;
    credit_remaining: number;
  };
  dispositions: { disposition: string; count: number }[];
  hourly: { hour: string; dialed: number; connected: number }[];
  responses: { response: string; count: number }[];
  progress: {
    total: number;
    dialed: number;
    remaining: number;
    pending: number;
    pending_retry: number;
    active: number;
    completed: number;
    failed: number;
    dnc: number;
  };
}

export async function getCampaignDashboard(
  campaignId: number,
  window: string = "24h",
): Promise<CampaignDashboard> {
  const resp = await api.get<CampaignDashboard>(
    `/reports/campaigns/${campaignId}/dashboard`,
    { params: { window } },
  );
  return resp.data;
}

// Mirrors app/routers/report.py transfer_durations() — per-campaign transfer
// call-duration histogram. Every transfer connected (a lead can only press on an
// answered call). Duration prefers the ANSWERED cdr leg's billsec, then falls back
// to the spool answered_at->updated_at span, because the cdr mis-stamps ~45% of
// real transfers as NO ANSWER / billsec 0. b_connected_nodur now counts only the
// rare transfer with neither signal; b_under_10s is only genuinely short calls.
export interface CampaignTransferDurations {
  campaign_id: number;
  campaign_name: string;
  transfers: number;
  b_connected_nodur: number;
  b_under_10s: number;
  b_10s_2m: number;
  b_2m_5m: number;
  b_5m_10m: number;
  b_10m_15m: number;
  b_15m_20m: number;
  b_over_20m: number;
}
export interface TransferDurations {
  window: string;
  buckets: string[];
  campaigns: CampaignTransferDurations[];
}
export async function getTransferDurations(window: string = "today"): Promise<TransferDurations> {
  const resp = await api.get<TransferDurations>("/reports/transfer-durations", {
    params: { window },
  });
  return resp.data;
}

// Which audio slot on a campaign. Mirrors app/routers/campaign.py _RECORDING_SLOTS.
export type RecordingSlot = "greeting" | "voicemail";

export interface RecordingSwapResult {
  campaign_id: number;
  slot: RecordingSlot;
  ref: string;
  // How long until callers hear it (the runner's config refresh). 0 when the
  // campaign isn't running, since there is nothing live to wait for.
  live_within_seconds: number;
}

// Point a campaign at different audio. Safe on a running campaign: it repoints
// the campaign rather than overwriting any file, so calls in progress finish on
// the old audio and recordings shared with other campaigns are untouched.
export async function swapCampaignRecording(
  campaignId: number,
  slot: RecordingSlot,
  ref: string,
): Promise<RecordingSwapResult> {
  const resp = await api.put<APIResponse<RecordingSwapResult>>(
    `/campaigns/${campaignId}/recording`,
    { slot, ref },
  );
  return unwrap(resp.data);
}

export interface RepCoachingCall {
  id: string;
  filename: string;
  call_type: string;
  phone: string;
  did: string;
  duration_sec: number;
  timestamp: string;
  transcript: string;
  stage: string;
  uniroma?: {
    id?: string;
    rep_connected?: boolean;
    outcome?: string;
    tax_debt_amount?: string;
    score?: number | null;
    what_rep_did_well?: string;
    what_rep_could_do_better?: string;
    summary?: string;
    key_quote?: string;
  };
}

export interface RepCoachingReport {
  available: boolean;
  generated_at?: string;
  date?: string;
  usr_id?: number;
  campaign_id?: number;
  funnel?: {
    total_recordings: number;
    outbound_transfers: number;
    inbound_callbacks: number;
    rep_conversations: number;
    hold_queue_drops: number;
    brief_contacts: number;
    ivr_hangups_pre_press3: number;
    short_hangups_under_10s: number;
    avg_rep_talk_sec: number;
  };
  synthesis?: {
    overall_rep_grade?: string;
    overall_rep_score_out_of_10?: number;
    executive_summary?: string;
    what_reps_did_well?: { title: string; detail: string }[];
    what_reps_could_do_better?: {
      title: string;
      problem: string;
      fix_script: string;
      impact?: string;
    }[];
    operational_bottlenecks?: {
      title: string;
      finding: string;
      recommendation: string;
    }[];
  };
  calls?: RepCoachingCall[];
}

export async function getRepCoaching(): Promise<RepCoachingReport> {
  const resp = await api.get<RepCoachingReport>("/reports/rep-coaching");
  return resp.data;
}

// ---------------------------------------------------------------------------
// Plain-English Campaign Builder
// ---------------------------------------------------------------------------

export interface BuilderOption {
  label: string;
  value: string;
  subtitle?: string | null;
}

export interface BuilderQuestion {
  field: string;
  question: string;
  options: BuilderOption[];
}

export interface BuilderChatTurn {
  role: "user" | "assistant";
  content: string;
}

export interface CampaignDraftProposal {
  name?: string | null;
  name_auto_generated?: boolean;
  recipe?: "press1_rescue" | "press1" | "press1_instant_sms" | "direct" | "broadcast";
  campaign_type?: "press1" | "broadcast" | "direct" | null;
  message_type?: number;
  contact_group_id?: number | null;
  contact_group_name?: string | null;
  contact_group_count?: number | null;
  did_group_id?: number | null;
  did_group_name?: string | null;
  did_group_count?: number | null;
  did_pool_forward_mode?: "same_as_transfer" | "keep" | "custom";
  did_pool_forward_target?: string | null;
  did_pool_inbound_mode?: "direct" | "press1" | "press3";
  recording_file?: string | null;
  recording_name?: string | null;
  recording_duration_sec?: number | null;
  am_recording_file?: string | null;
  am_recording_name?: string | null;
  connect_key?: string;
  connect_key_confirmed?: boolean;
  optout_key?: string;
  transfer_target?: "pstn" | "agents";
  transfer_extension_id?: number | null;
  transfer_extension_name?: string | null;
  transfer_destination_display?: string | null;
  new_transfer_forward_to?: string | null;
  new_transfer_extension_name?: string | null;
  callback_rescue_enabled?: boolean;
  cta_target_min_duration_sec?: number;
  callback_rescue_sms_text?: string | null;
  callback_rescue_sms_from?: string | null;
  callback_did_id?: number | null;
  callback_did_phone?: string | null;
  auto_buy_toll_free?: boolean;
  callback_did_route_mode?: "keep" | "same_as_transfer" | "inbound_queue" | "sip_uri" | "external_number";
  callback_did_route_target?: string | null;
  press1_sms_enabled?: boolean;
  press1_sms_text?: string | null;
  channel?: number;
  ring_time_sec?: number;
  try_allowed?: number;
  local_dial?: boolean;
  am_action?: string;
  lnp_filter_mode?: "off" | "include" | "exclude";
  lnp_filter_carriers?: string;
  lnp_require_mobile?: string;
  lnp_require_valid?: string;
  vm_skip_mode?: "off" | "on";
  vm_skip_min_calls?: string;
}

export interface BuilderInventoryResponse {
  contact_groups: { id: number; name: string; count: number }[];
  did_groups: {
    id: number;
    name: string;
    count: number;
    forwarded_count?: number;
    unforwarded_count?: number;
    action_type?: string | null;
    action_data?: string | null;
    inbound_mode?: "direct" | "press1" | "press3";
  }[];
  recordings: {
    ref: string;
    filename: string;
    name: string;
    nickname?: string | null;
    duration_sec?: number | null;
    transcription?: string | null;
    kind?: "greeting" | "voicemail";
    detected_key?: string | null;
    detected_optout_key?: string | null;
    embedded_callback_phone?: string | null;
  }[];
  extensions: {
    id: number;
    name: string;
    title?: string | null;
    forward_to?: string | null;
  }[];
  callback_dids: {
    id: number;
    phone: string;
    formatted: string;
    name: string;
    is_toll_free: boolean;
    is_protected: boolean;
    action_type?: string | null;
    action_data?: string | null;
  }[];
  sms_defaults: {
    callback_rescue_sms_text?: string | null;
    callback_rescue_sms_from?: string | null;
    cta_target_min_duration_sec?: number;
    press1_sms_text?: string | null;
  };
  last_campaign_blueprint?: CampaignDraftProposal;
  self_healed_notes?: string[];
}

export interface BuilderAnalyzeResponse {
  reply: string;
  draft: CampaignDraftProposal;
  questions: BuilderQuestion[];
  warnings: string[];
  missing_fields: string[];
  ready_to_create: boolean;
  summary_bullets: string[];
}

export interface BuilderCommitResponse {
  campaign_id: number;
  transfer_extension_id?: number | null;
  auto_created_extension?: boolean;
  did_pool_forwarded?: boolean;
  did_pool_updated_dids?: number;
  did_pool_action_type?: string | null;
  did_pool_action_data?: string | null;
  did_pool_inbound_mode?: string | null;
  callback_did_updated?: boolean;
  auto_bought_toll_free?: boolean;
  callback_did_id?: number | null;
  callback_did_phone?: string | null;
  healed_notes?: string[];
}

export async function getCampaignBuilderInventory(): Promise<BuilderInventoryResponse> {
  const resp = await api.get<APIResponse<BuilderInventoryResponse>>(
    "/campaigns/ai-builder/inventory",
  );
  return unwrap(resp.data);
}

export async function analyzeCampaignBuilder(payload: {
  message: string;
  history: BuilderChatTurn[];
  draft: CampaignDraftProposal | null;
}): Promise<BuilderAnalyzeResponse> {
  const resp = await api.post<APIResponse<BuilderAnalyzeResponse>>(
    "/campaigns/ai-builder/analyze",
    payload,
  );
  return unwrap(resp.data);
}

export async function commitCampaignBuilder(
  draft: CampaignDraftProposal,
): Promise<BuilderCommitResponse> {
  const resp = await api.post<APIResponse<BuilderCommitResponse>>(
    "/campaigns/ai-builder/commit",
    { draft },
  );
  return unwrap(resp.data);
}


